import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import Layout, { Section, PageHero } from "@/components/layout/Layout";
import { Reveal } from "@/components/common/Reveal";
import { api } from "@/lib/api";
import { ArrowUpRight } from "lucide-react";

export default function Careers() {
    const { t } = useTranslation();
    const [roles, setRoles] = useState([]);
    useEffect(() => { api.get("/roles").then((r) => setRoles(r.data?.items || [])); }, []);

    const values = t("body.careers.values", { returnObjects: true }) || [];
    const lookLabel = t("body.careers.lookLabel");
    const lookTitle = t("body.careers.lookTitle");
    const openLabel = t("body.careers.openLabel");
    const openTitle = t("body.careers.openTitle");
    const positionsSuffix = t("body.careers.positionsSuffix");

    return (
        <Layout title="Careers — Ventara" description="Build the calm infrastructure of the next decade.">
            <PageHero i18nKey="careers"
                eyebrow="Careers"
                title={<>Build the calm<br />layer with us.</>}
                lead="We are a small studio hiring in patient, deliberate rounds. If you care about the shape of AI as much as the size of it, we would like to meet you."
            />

            <Section className="pb-24 grid lg:grid-cols-12 gap-16">
                <div className="lg:col-span-6">
                    <img src="/careers-team.webp"
                         alt="The Ventara team collaborating around a laptop in the studio" className="w-full rounded-3xl aspect-[4/5] object-cover grayscale-[15%]" loading="lazy" />
                </div>
                <div className="lg:col-span-6">
                    <p className="label" data-testid="careers-look-label">{lookLabel}</p>
                    <h2 className="font-serif font-light text-4xl sm:text-5xl mt-6 tracking-tight leading-[1.05]">
                        {lookTitle}
                    </h2>
                    <div className="mt-12 space-y-6">
                        {values.map((v, i) => (
                            <div key={i} className="border-t border-border pt-6" data-testid={`careers-value-${i}`}>
                                <div className="font-serif text-xl tracking-tight">{v.t}</div>
                                <div className="prose-body text-sm mt-2">{v.d}</div>
                            </div>
                        ))}
                    </div>
                </div>
            </Section>

            <Section className="py-24 border-t border-border">
                <div className="flex items-end justify-between mb-10">
                    <div>
                        <p className="label" data-testid="careers-open-label">{openLabel}</p>
                        <h2 className="font-serif font-light text-4xl sm:text-5xl mt-4 tracking-tight">{openTitle}</h2>
                    </div>
                    <div className="hidden sm:block label">{roles.length} {positionsSuffix}</div>
                </div>
                <div className="border-t border-border">
                    {roles.map((r, i) => (
                        <Reveal key={i}>
                            <div className="grid lg:grid-cols-12 gap-6 py-8 border-b border-border group hover:bg-muted/30 -mx-6 sm:-mx-10 px-6 sm:px-10 transition-colors cursor-pointer" data-testid={`role-${i}`}>
                                <div className="lg:col-span-1 label pt-2">0{i + 1}</div>
                                <div className="lg:col-span-6">
                                    <Link to={`/careers/${r.id || i}`} className="font-serif text-2xl tracking-tight hover:text-foreground">{r.t || r.title}</Link>
                                </div>
                                <div className="lg:col-span-2 label pt-2">{r.team}</div>
                                <div className="lg:col-span-2 label pt-2">{r.loc}</div>
                                <div className="lg:col-span-1 flex items-start justify-end pt-2">
                                    <Link to={`/careers/${r.id || i}`}><ArrowUpRight className="h-5 w-5 text-muted-foreground group-hover:text-foreground transition-colors" /></Link>
                                </div>
                            </div>
                        </Reveal>
                    ))}
                </div>
            </Section>
        </Layout>
    );
}
